import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { initialApplicationsState } from '@store/applications/applications.initial-state';
import { applicationsActions } from '@store/applications/applications.slice';

import { RouteEnum } from '@enum/route.enum';
import { useRouteName } from '@hook/route/use-route-name.hook';
import { useStoreDispatch, useStoreSelector } from '@hook/store/store.hooks';
import { useUserMeta } from '@hook/user/use-user-meta.hook';
import { isExist, isString } from '@util/data/is-exist';

const SKIP_REDIRECT_ROUTING = new Set([RouteEnum.Dashboard.name]);

export const useCurrentApplication = (applicationId = '') => {
  const dispatch = useStoreDispatch();
  const navigate = useNavigate();
  const {
    data,
    isLoaded,
    isLoading,
    meta: { currentId, accessToken },
  } = useStoreSelector((state) => state.applications);
  const {
    meta: { applicationId: applicationLocalId = '' },
  } = useUserMeta();
  const name = useRouteName();
  const accounts = useStoreSelector((state) => Object.values(state.accounts.data));

  const current = data[applicationId] ?? data[currentId] ?? data[applicationLocalId] ?? Object.values(data).at(0);

  useEffect(
    () =>
      void (
        isExist(current) &&
        currentId !== current.id &&
        dispatch(applicationsActions.setCurrentApplication(current.id))
      ),
    [current?.id, currentId],
  );

  const onChange = useCallback(
    (value: string) => {
      dispatch(applicationsActions.setCurrentApplication(value));
      if (!SKIP_REDIRECT_ROUTING.has(name)) navigate(RouteEnum.Dashboard.path);
    },
    [name, dispatch, navigate],
  );

  return {
    count: Object.keys(data).length,
    isLoading,
    isLoaded: isLoaded && data !== initialApplicationsState.data,
    current,
    onChange,
    accessToken,
    hasToken: isString(accessToken),
    accounts,
  };
};
